import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { environment } from '../../environments/environment';

import {
  CreateOpCommentBody,
  CreateOpReportBody,
  OpComment,
  OpCommentWire,
  OpCommentWireBody,
  OpReport,
  OpReportContent,
  OpReportWire,
  OpReportWireBody,
  UpdateOpReportBody,
} from './operational-report.model';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

// op-reports und op-comments laufen gegen das echte (aber vom
// abgestimmten Contract abweichende) BFF – siehe die ausführlichen
// Kommentare bei OpReportWire/OpReportWireBody bzw. OpCommentWire/
// OpCommentWireBody in operational-report.model.ts für die Details der
// Abweichung und mapWireToReport()/mapWireToComment() unten fürs Mapping.
//
// Für op-comments gibt es bisher nur GET (Liste) und PUT (Neuanlage) –
// ein Update-Endpunkt ist vom Backend nicht spezifiziert und von der App
// wird aktuell keine Kommentar-Bearbeitung angeboten.

@Injectable({
  providedIn: 'root',
})
export class OperationalReportService {
  private http = inject(HttpClient);

  private readonly baseUrl = environment.url.bffV3 + '/customized';

  // Zuletzt vom Backend geladene Rohdaten je Report-id. Wird für
  // updateReport() gebraucht: POST ersetzt laut Backend-Kollege die
  // komplette Zeile statt nur die geänderten Felder zu patchen, d.h.
  // createUser/createDtg/die Snapshot-Strings (egp/egw/epp/smw) müssen
  // beim reinen Update unverändert mitgeschickt werden, sonst würden sie
  // überschrieben. getReports()/getReport() füllen diesen Cache; die
  // Bearbeiten-Ansicht lädt einen Report immer erst per getReport(),
  // bevor updateReport() überhaupt aufgerufen werden kann.
  private wireCache = new Map<number, OpReportWire>();

  // ---------------------------------------------------------------------
  // op-reports
  // ---------------------------------------------------------------------

  public getReports(
    customerId: number,
    startDtg: string,
    endDtg: string
  ): Observable<OpReport[]> {
    // Die Liste kennt (noch) keinen Zeitraum-Filter und liefert immer ALLE
    // Einträge des Kunden zurück (laut Backend-Kollege bewusst so, die
    // Menge bleibt überschaubar) – gefiltert wird deshalb weiterhin
    // clientseitig, nach dem Start-Zeitpunkt des Einsatzes: ein Eintrag
    // erscheint, wenn SEIN Start im gewählten Zeitraum liegt – unabhängig
    // davon, wie lange er dauert oder ob er noch offen ist.
    const start = moment(startDtg);
    const end = moment(endDtg);
    return this.http
      .get<OpReportWire[]>(`${this.baseUrl}/reports/${customerId}/list`)
      .pipe(
        map(wireReports => {
          wireReports.forEach(wire => this.wireCache.set(wire.id, wire));
          return wireReports
            .filter(
              wire =>
                moment(wire.createDtg).isSameOrAfter(start) &&
                moment(wire.createDtg).isSameOrBefore(end)
            )
            .map(wire => this.mapWireToReport(wire));
        })
      );
  }

  public getReport(
    customerId: number,
    id: number
  ): Observable<OpReport | undefined> {
    // customerId wird im Pfad nicht gebraucht (laut Backend-Kollege),
    // bleibt aber im Methoden-Signatur, damit die Call-Sites unverändert
    // bleiben.
    return this.http.get<OpReportWire>(`${this.baseUrl}/report/${id}`).pipe(
      map(wire => {
        this.wireCache.set(wire.id, wire);
        return this.mapWireToReport(wire);
      })
    );
  }

  public createReport(
    customerId: number,
    body: CreateOpReportBody
  ): Observable<OpReport> {
    // Das Backend nimmt beim Anlegen (PUT) keinen abgeschlossenen Eintrag
    // an. Ein direkt zu schließender Einsatz wird deshalb erst offen per PUT
    // angelegt und danach per POST (updateReport()) auf dieselbe id
    // abgeschlossen.
    const closeAfterCreate = body.content.status === 'closed';
    const wireBody: OpReportWireBody = {
      customerId,
      createUser: body.content.leaderName,
      closeUser: closeAfterCreate ? null : (body.content.closedByName ?? null),
      createDtg: body.startDtg,
      endDtg: closeAfterCreate ? null : body.endDtg,
      status: closeAfterCreate ? 'open' : body.content.status,
      text: body.content.situationReport,
      keywords: body.content.categories.map(category => category.key),
      egp: this.encodeJsonField(body.egp),
      egw: this.encodeJsonField(body.egw),
      epp: this.encodeJsonField(body.epp),
      smw: this.encodeJsonField(body.smw),
    };
    // TODO: entfernen, sobald der 500er bei PUT /report geklärt ist.
    console.log('PUT /report body:', wireBody);
    // Das Backend antwortet auf den PUT nur mit der neuen id (z.B. 44), nicht
    // mit dem vollständigen Eintrag. Der anschließende POST wird deshalb
    // komplett aus den Anlegedaten gebaut und nicht aus einer Antwort/Cache.
    return this.http.put<number>(`${this.baseUrl}/report`, wireBody).pipe(
      switchMap(id => {
        if (!closeAfterCreate) {
          return of(this.mapCreatedReport(customerId, id, body));
        }
        const closeBody: OpReportWireBody = {
          ...wireBody,
          id,
          closeUser: body.content.closedByName ?? null,
          endDtg: body.endDtg,
          status: 'closed',
        };
        console.log('POST /report body:', closeBody);
        return this.http
          .post<unknown>(`${this.baseUrl}/report`, closeBody)
          .pipe(map(() => this.mapCreatedReport(customerId, id, body)));
      })
    );
  }

  private mapCreatedReport(
    customerId: number,
    id: number,
    body: CreateOpReportBody
  ): OpReport {
    return {
      id,
      customerId,
      start: body.startDtg,
      end: body.endDtg,
      content: body.content,
      egp: body.egp,
      egw: body.egw,
      epp: body.epp,
      smw: body.smw,
    };
  }

  public updateReport(
    customerId: number,
    id: number,
    body: UpdateOpReportBody
  ): Observable<OpReport> {
    const cached = this.wireCache.get(id);
    if (!cached) {
      // Sollte beim aktuellen App-Flow nicht vorkommen (die Bearbeiten-
      // Ansicht lädt den Report immer zuerst per getReport()) – ohne
      // gecachte Rohdaten gehen egp/egw/epp/smw bei diesem Update verloren,
      // da POST die komplette Zeile ersetzt statt nur zu patchen.
      console.error(
        `updateReport(${id}): kein zuvor geladener Report im Cache – ` +
          'egp/egw/epp/smw werden dadurch mit null überschrieben.'
      );
    }

    const wireBody: OpReportWireBody = {
      id,
      customerId,
      createUser: body.content.leaderName,
      closeUser: body.content.closedByName ?? cached?.closeUser ?? null,
      createDtg: cached?.createDtg ?? moment().toISOString(),
      endDtg: body.endDtg,
      status: body.content.status,
      text: body.content.situationReport,
      keywords: body.content.categories.map(category => category.key),
      // Nicht die gecachten Rohstrings 1:1 durchreichen, sondern über
      // decodeJsonField()/encodeJsonField() decodieren und neu kodieren –
      // stellt sicher, dass hier exakt dieselbe (doppelte) Kodierung
      // rausgeht wie bei createReport(), unabhängig davon, in welcher Form
      // die Rohdaten gecacht sind.
      egp: this.encodeJsonField(this.decodeJsonField(cached?.egp ?? null)),
      egw: this.encodeJsonField(this.decodeJsonField(cached?.egw ?? null)),
      epp: this.encodeJsonField(this.decodeJsonField(cached?.epp ?? null)),
      smw: this.encodeJsonField(this.decodeJsonField(cached?.smw ?? null)),
    };

    // TODO: entfernen, sobald der 500er bei POST /report geklärt ist.
    console.log('POST /report body:', wireBody);
    return this.http
      .post<OpReportWire>(`${this.baseUrl}/report`, wireBody)
      .pipe(
        map(wire => {
          this.wireCache.set(wire.id, wire);
          return this.mapWireToReport(wire);
        })
      );
  }

  // ---------------------------------------------------------------------
  // op-comments
  // ---------------------------------------------------------------------

  public getComments(opReportId: number): Observable<OpComment[]> {
    return this.http
      .get<OpCommentWire[]>(`${this.baseUrl}/reportcomments/${opReportId}`)
      .pipe(
        map(wireComments =>
          wireComments.map(wire => this.mapWireToComment(wire))
        )
      );
  }

  public createComment(
    opReportId: number,
    body: CreateOpCommentBody
  ): Observable<OpComment> {
    const wireBody: OpCommentWireBody = {
      reportId: opReportId,
      createUser: body.content.authorName ?? '',
      content: this.encodeJsonField({ text: body.content.text }) ?? '',
    };
    console.log('POST /report body:', wireBody);
    return this.http
      .put<OpCommentWire>(`${this.baseUrl}/reportcomment`, wireBody)
      .pipe(map(wire => this.mapWireToComment(wire)));
  }

  // ---------------------------------------------------------------------
  // Wire-Mapping (echtes BFF ↔ interne OpReport/OpComment-Modelle) – siehe
  // die Kommentare bei OpReportWire/OpCommentWire in
  // operational-report.model.ts.
  // ---------------------------------------------------------------------

  private mapWireToReport(wire: OpReportWire): OpReport {
    const content: OpReportContent = {
      status: wire.status,
      categories: (wire.keywords ?? []).map(key => ({ key })),
      situationReport: wire.text,
      // Es gibt nur den einen Namen (createUser) – siehe Modell-Kommentar
      // bei OpReportWire. Für openedByName (Bearbeitungs-Timeline) wird
      // derselbe Name verwendet.
      leaderName: wire.createUser,
      openedByName: wire.createUser,
      closedByName: wire.closeUser ?? undefined,
    };

    return {
      id: wire.id,
      customerId: wire.customerId,
      start: wire.createDtg,
      end: wire.endDtg,
      content,
      egp: this.decodeJsonField(wire.egp),
      egw: this.decodeJsonField(wire.egw),
      epp: this.decodeJsonField(wire.epp),
      smw: this.decodeJsonField(wire.smw),
    };
  }

  private mapWireToComment(wire: OpCommentWire): OpComment {
    const content = this.decodeJsonField<{ text: string }>(wire.content);

    return {
      id: wire.id,
      opReportId: wire.reportId,
      content: {
        text: content?.text ?? '',
        createdAt: wire.createDtg,
        authorName: wire.createUser,
      },
    };
  }

  // egp/egw/epp/smw (und laut Kollege auch op-comments.content) erwarten
  // beim Schreiben doppelt JSON-kodierte Strings: einmal das eigentliche
  // Objekt, dann nochmal als Zeichenkette – aber ohne das äußere
  // Anführungszeichen-Paar, das ein normales JSON.stringify(string)
  // mitliefern würde (das kommt erst durch die normale Serialisierung des
  // gesamten Request-Bodys dazu). Ein einfach kodiertes Objekt führt beim
  // Speichern zu einem 500er.
  //
  // Asymmetrisch beim Backend: beim Lesen (GET) kommen dieselben Felder
  // dagegen nur einfach kodiert zurück – bestätigt an echten gespeicherten
  // egw/epp/smw-Werten. decodeJsonField() parst deshalb nur einmal, obwohl
  // encodeJsonField() doppelt kodiert.
  private decodeJsonField<T>(raw: string | null): T | null {
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as T;
    } catch {
      // Backend-Testdaten enthalten teils Platzhalter statt echtem JSON
      // (z.B. "12") – dann lieber null als ein kaputtes Objekt.
      return null;
    }
  }

  private encodeJsonField(value: unknown): string | null {
    if (!value) {
      return null;
    }
    return JSON.stringify(JSON.stringify(value)).slice(1, -1);
  }
}
